import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, setToken } from "../../lib/api";
import { LogOut, Trash2, Plus, Save, RefreshCw, Mail } from "lucide-react";
import ClientsManager from "./ClientsManager";
import FormTemplatesManager from "./FormTemplatesManager";

const COLLECTIONS = [
  { key: "projects", label: "Progetti (menu)", id: "slug", fields: ["slug", "title", "short_desc", "image", "banner_image", "link", "content", "status", "order"] },
  { key: "blog_posts", label: "Articoli (archivio)", id: "slug", fields: ["slug", "title", "category", "image", "url", "meta_title", "meta_description", "excerpt", "content", "published", "featured"] },
  { key: "services", label: "Servizi", id: "slug", fields: ["slug", "name", "icon", "image", "short", "long"] },
  { key: "team", label: "Team", id: "id", fields: ["name", "role", "photo", "bio"] },
  { key: "categories", label: "Categorie", id: "slug", fields: ["slug", "name"] },
];

const HOMEPAGE_FIELDS = [
  "hero_overline", "hero_title", "hero_subtitle", "hero_cta_primary", "hero_cta_secondary", "hero_image",
  "intro_overline", "intro_title", "intro_body", "intro_image",
  "pillar_overline", "pillar_title", "pillar_excerpt", "pillar_cta",
  "method_overline", "method_title",
  "final_cta_overline", "final_cta_title", "final_cta_subtitle", "final_cta_button",
];

const SETTINGS_FIELDS = ["company_name", "tagline", "address", "email", "pec", "vat", "phone", "footer_legal"];
const CHI_SIAMO_FIELDS = ["hero_overline", "hero_title", "intro_p1", "intro_p2", "intro_p3", "intro_image", "mission_title", "mission_body", "vision_title", "vision_body", "values_json", "cta_title", "cta_subtitle"];
const LEGAL_FIELDS = ["title", "content"];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("projects");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [homepage, setHomepage] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [pillar, setPillar] = useState(null);
  const [settings, setSettings] = useState(null);
  const [chiSiamo, setChiSiamo] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [cookie, setCookie] = useState(null);
  const [srh01, setSrh01] = useState(null);

  useEffect(() => {
    api.get("/auth/me").catch(() => { setToken(null); nav("/admin/login"); });
  }, [nav]);

  const reload = async () => {
    if (tab === "homepage") {
      const r = await api.get("/homepage"); setHomepage(r.data);
    } else if (tab === "contacts") {
      const r = await api.get("/admin/contacts"); setContacts(r.data);
    } else if (tab === "pillar") {
      const r = await api.get("/pillar").catch(() => ({ data: null })); setPillar(r?.data || { content: "" });
    } else if (tab === "settings") {
      const r = await api.get("/settings"); setSettings(r.data || {});
    } else if (tab === "chi-siamo") {
      const r = await api.get("/page/chi-siamo").catch(() => ({ data: {} })); setChiSiamo(r.data || {});
    } else if (tab === "privacy") {
      const r = await api.get("/page/privacy").catch(() => ({ data: {} })); setPrivacy(r.data || {});
    } else if (tab === "cookie") {
      const r = await api.get("/page/cookie").catch(() => ({ data: {} })); setCookie(r.data || {});
    } else if (tab === "srh01") {
      const r = await api.get("/page/srh01").catch(() => ({ data: {} })); setSrh01(r.data || {});
    } else if (tab === "clienti" || tab === "modelli") {
      // Managers handle their own data
      return;
    } else {
      const r = await api.get(`/admin/${tab}`); setItems(r.data);
    }
    setEditing(null);
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab]);

  const current = COLLECTIONS.find((c) => c.key === tab);

  const save = async () => {
    if (tab === "homepage") { await api.put("/admin/homepage", { data: homepage }); await reload(); return; }
    if (tab === "pillar") { await api.put("/admin/pillar", { data: pillar }); await reload(); return; }
    if (tab === "settings") { await api.put("/admin/settings", { data: settings }); await reload(); return; }
    if (tab === "chi-siamo") { await api.put("/admin/page/chi-siamo", { data: chiSiamo }); await reload(); return; }
    if (tab === "privacy") { await api.put("/admin/page/privacy", { data: privacy }); await reload(); return; }
    if (tab === "cookie") { await api.put("/admin/page/cookie", { data: cookie }); await reload(); return; }
    if (tab === "srh01") { await api.put("/admin/page/srh01", { data: srh01 }); await reload(); return; }
    const idVal = editing[current.id];
    if (items.find((x) => x[current.id] === idVal)) {
      await api.put(`/admin/${tab}/${idVal}`, { data: editing });
    } else {
      await api.post(`/admin/${tab}`, { data: editing });
    }
    await reload();
  };

  const remove = async (val) => {
    if (!window.confirm("Eliminare definitivamente?")) return;
    await api.delete(`/admin/${tab}/${val}`); await reload();
  };

  const logout = () => { setToken(null); nav("/admin/login"); };

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-[#F9FAFB]">
      <header className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/assets/logo-betelgez.png" className="h-10" alt="Betelgez" />
            <span className="text-sm font-semibold text-[#007A3D]">CMS</span>
          </Link>
          <button onClick={logout} data-testid="admin-logout" className="text-sm text-[#6B7280] hover:text-[#1A1A1A] inline-flex items-center gap-2"><LogOut className="w-4 h-4" /> Esci</button>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-wrap gap-2 mb-6">
          {["settings", "homepage", "chi-siamo", "pillar", "srh01", "clienti", "modelli", ...COLLECTIONS.map((c) => c.key), "privacy", "cookie", "contacts"].map((k) => (
            <button key={k} data-testid={`admin-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2 rounded-full text-sm font-medium border ${tab === k ? "bg-[#007A3D] text-white border-[#007A3D]" : "bg-white border-[#E5E7EB] text-[#1A1A1A] hover:border-[#00A651]"}`}>
              {k === "homepage" ? "Homepage" : k === "pillar" ? "Pillar SEO" : k === "contacts" ? "Contatti ricevuti" : k === "settings" ? "Impostazioni Sito" : k === "chi-siamo" ? "Chi Siamo" : k === "privacy" ? "Privacy" : k === "cookie" ? "Cookie" : k === "srh01" ? "SRH01" : k === "clienti" ? "Area Riservata" : k === "modelli" ? "Modelli Compilabili" : COLLECTIONS.find((c) => c.key === k)?.label}
            </button>
          ))}
        </div>

        {tab === "clienti" && (
          <ClientsManager />
        )}

        {tab === "modelli" && (
          <FormTemplatesManager />
        )}

        {tab === "settings" && settings && (
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
            <h2 className="font-display font-bold text-2xl text-[#1A1A1A] mb-2">Impostazioni Sito</h2>
            <p className="text-sm text-[#6B7280] mb-6">Dati visibili nel footer, header, contatti.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {SETTINGS_FIELDS.map((f) => (
                <div key={f} className={["tagline"].includes(f) ? "md:col-span-2" : ""}>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-2">{f}</label>
                  <textarea rows={f === "tagline" ? 3 : 1} value={settings[f] || ""} onChange={(e) => setSettings({ ...settings, [f]: e.target.value })}
                    className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF8F0] focus:border-[#00A651]" />
                </div>
              ))}
            </div>
            <button onClick={save} className="mt-6 inline-flex items-center gap-2 bg-[#00A651] text-white rounded-full px-6 py-3 font-medium"><Save className="w-4 h-4" /> Salva</button>
          </div>
        )}

        {tab === "chi-siamo" && chiSiamo && (
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
            <h2 className="font-display font-bold text-2xl text-[#1A1A1A] mb-2">Pagina Chi Siamo</h2>
            <p className="text-sm text-[#6B7280] mb-6">Per i valori: JSON array. Esempio: <code>[{"{"}"t":"Titolo","d":"Descrizione"{"}"}]</code></p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {CHI_SIAMO_FIELDS.map((f) => (
                <div key={f} className={["intro_p1", "intro_p2", "intro_p3", "mission_body", "vision_body", "cta_subtitle", "values_json"].includes(f) ? "md:col-span-2" : ""}>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-2">{f}</label>
                  <textarea rows={["intro_p1", "intro_p2", "intro_p3", "mission_body", "vision_body", "values_json"].includes(f) ? 4 : 1} value={chiSiamo[f] || ""} onChange={(e) => setChiSiamo({ ...chiSiamo, [f]: e.target.value })}
                    className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF8F0] focus:border-[#00A651] font-mono text-sm" />
                </div>
              ))}
            </div>
            <button onClick={save} className="mt-6 inline-flex items-center gap-2 bg-[#00A651] text-white rounded-full px-6 py-3 font-medium"><Save className="w-4 h-4" /> Salva</button>
          </div>
        )}

        {(tab === "privacy" || tab === "cookie") && (
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
            <h2 className="font-display font-bold text-2xl text-[#1A1A1A] mb-6">{tab === "privacy" ? "Privacy Policy" : "Cookie Policy"}</h2>
            {LEGAL_FIELDS.map((f) => {
              const obj = tab === "privacy" ? privacy : cookie;
              const setObj = tab === "privacy" ? setPrivacy : setCookie;
              return obj ? (
                <div key={f} className="mb-4">
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-2">{f}</label>
                  <textarea rows={f === "content" ? 18 : 1} value={obj[f] || ""} onChange={(e) => setObj({ ...obj, [f]: e.target.value })}
                    className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 font-mono text-sm" />
                </div>
              ) : null;
            })}
            <button onClick={save} className="mt-2 inline-flex items-center gap-2 bg-[#00A651] text-white rounded-full px-6 py-3 font-medium"><Save className="w-4 h-4" /> Salva</button>
          </div>
        )}

        {tab === "srh01" && srh01 && (
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
            <h2 className="font-display font-bold text-2xl text-[#1A1A1A] mb-6">SRH01 Servizi di Consulenza</h2>
            {["loghi_image", "title", "subtitle", "box1_title", "box1_text", "box2_title", "box2_text"].map((f) => (
              <div key={f} className="mb-4">
                <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-2">{f}</label>
                <textarea rows={["box1_text", "box2_text"].includes(f) ? 5 : 1} value={srh01[f] || ""} onChange={(e) => setSrh01({ ...srh01, [f]: e.target.value })}
                  className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF8F0] focus:border-[#00A651]" />
              </div>
            ))}
            <button onClick={save} className="mt-2 inline-flex items-center gap-2 bg-[#00A651] text-white rounded-full px-6 py-3 font-medium"><Save className="w-4 h-4" /> Salva</button>
          </div>
        )}

        {tab === "homepage" && homepage && (
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
            <h2 className="font-display font-bold text-2xl text-[#1A1A1A] mb-6">Homepage</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {HOMEPAGE_FIELDS.map((f) => (
                <div key={f} className={["hero_subtitle", "intro_body", "pillar_excerpt", "final_cta_subtitle"].includes(f) ? "md:col-span-2" : ""}>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-2">{f}</label>
                  <textarea rows={["hero_subtitle", "intro_body", "pillar_excerpt", "final_cta_subtitle"].includes(f) ? 3 : 1}
                    value={homepage[f] || ""} onChange={(e) => setHomepage({ ...homepage, [f]: e.target.value })}
                    className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF8F0] focus:border-[#00A651]" />
                </div>
              ))}
            </div>
            <button onClick={save} data-testid="admin-homepage-save" className="mt-8 inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#007A3D] text-white rounded-full px-6 py-3 font-medium"><Save className="w-4 h-4" /> Salva</button>
          </div>
        )}

        {tab === "pillar" && pillar && (
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
            <h2 className="font-display font-bold text-2xl text-[#1A1A1A] mb-2">Pagina Pilastro SEO</h2>
            <p className="text-sm text-[#6B7280] mb-4">Markdown completo. Generato automaticamente con AI all’avvio se vuoto.</p>
            <input value={pillar.title || ""} onChange={(e) => setPillar({ ...pillar, title: e.target.value })} placeholder="Titolo" className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 mb-3" />
            <input value={pillar.meta_description || ""} onChange={(e) => setPillar({ ...pillar, meta_description: e.target.value })} placeholder="Meta description" className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 mb-3" />
            <textarea rows={24} value={pillar.content || ""} onChange={(e) => setPillar({ ...pillar, content: e.target.value })}
              className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 font-mono text-sm" />
            <button onClick={save} className="mt-6 inline-flex items-center gap-2 bg-[#00A651] text-white rounded-full px-6 py-3 font-medium"><Save className="w-4 h-4" /> Salva</button>
          </div>
        )}

        {tab === "contacts" && (
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 md:p-8">
            <h2 className="font-display font-bold text-2xl text-[#1A1A1A] mb-6">Contatti ricevuti</h2>
            {contacts.length === 0 ? <p className="text-[#6B7280]">Nessun contatto al momento.</p> : (
              <ul className="divide-y divide-[#E5E7EB]">
                {contacts.map((c) => (
                  <li key={c.id} className="py-4 flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="font-semibold text-[#1A1A1A]">{c.nome} <span className="font-normal text-[#6B7280]">— {c.email}</span></div>
                      <div className="text-xs text-[#6B7280]">{c.telefono} · {new Date(c.created_at).toLocaleString("it-IT")}</div>
                      <p className="mt-2 text-sm text-[#374151] whitespace-pre-wrap">{c.messaggio}</p>
                    </div>
                    <button onClick={async () => { await api.delete(`/admin/contacts/${c.id}`); reload(); }} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {current && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 bg-white rounded-3xl border border-[#E5E7EB] p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1A1A1A]">{current.label}</h3>
                <button data-testid="admin-new" onClick={() => setEditing(Object.fromEntries(current.fields.map((f) => [f, f === "published" ? true : f === "status" ? "published" : f === "order" ? 0 : ""])))}
                  className="inline-flex items-center gap-1.5 bg-[#EAF8F0] text-[#007A3D] rounded-full px-3 py-1.5 text-xs font-medium hover:bg-[#00A651] hover:text-white"><Plus className="w-3.5 h-3.5" /> Nuovo</button>
              </div>
              <ul className="divide-y divide-[#E5E7EB]">
                {items.map((it) => (
                  <li key={it[current.id]} className="py-3 flex items-center justify-between gap-3">
                    <button onClick={() => setEditing({ ...it })} className="text-left flex-1 text-sm text-[#1A1A1A] hover:text-[#007A3D]">{it.title || it.name || it.slug}</button>
                    <button onClick={() => remove(it[current.id])} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </li>
                ))}
                {items.length === 0 && <li className="py-3 text-sm text-[#6B7280]">Nessun elemento.</li>}
              </ul>
            </div>
            <div className="lg:col-span-7 bg-white rounded-3xl border border-[#E5E7EB] p-6">
              {editing ? (
                <>
                  <div className="space-y-4">
                    {current.fields.map((f) => (
                      <div key={f}>
                        <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#6B7280] mb-2">{f}</label>
                        {f === "published" || f === "featured" ? (
                          <select value={String(editing[f] ?? (f === "published"))} onChange={(e) => setEditing({ ...editing, [f]: e.target.value === "true" })}
                            className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2">
                            <option value="true">{f === "featured" ? "In evidenza" : "Pubblicato"}</option>
                            <option value="false">{f === "featured" ? "Normale" : "Bozza"}</option>
                          </select>
                        ) : f === "status" ? (
                          <select value={editing[f] ?? "published"} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })}
                            className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2">
                            <option value="published">Pubblicato</option>
                            <option value="draft">Bozza</option>
                          </select>
                        ) : f === "order" ? (
                          <input type="number" value={editing[f] ?? 0} onChange={(e) => setEditing({ ...editing, [f]: parseInt(e.target.value, 10) || 0 })}
                            className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2" />
                        ) : (
                          <textarea rows={["content", "long", "bio", "text", "short_desc"].includes(f) ? 8 : 1}
                            value={editing[f] ?? ""} onChange={(e) => setEditing({ ...editing, [f]: e.target.value })}
                            className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 font-mono text-sm" />
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 flex gap-2">
                    <button onClick={save} data-testid="admin-save" className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#007A3D] text-white rounded-full px-6 py-3 font-medium"><Save className="w-4 h-4" /> Salva</button>
                    <button onClick={() => setEditing(null)} className="rounded-full px-6 py-3 text-[#6B7280]">Annulla</button>
                  </div>
                </>
              ) : (
                <div className="text-center text-[#6B7280] py-12">Seleziona un elemento o crea “Nuovo”.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
